import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { PhotoPicker } from "@/components/PhotoPicker";
import { AdminProductDetails, isAiPhoto, marginOf } from "@/components/AdminProductDetails";
import { lowMargin } from "@/components/AdminMargins";
import { AiVerdictButton } from "@/components/AdminAiDescription";

const input = "w-full px-3 py-2 rounded-xl border border-[#E7E0D3] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E06D44]/40";
const EDITABLE = ["name", "price", "image", "description", "category", "shop_category", "verdict", "kiddee_fav", "active", "materials", "size", "care", "dog_size", "gallery", "cost", "ship_cost", "gtin", "mpn"];
const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const num = (v) => (v === "" || v == null ? null : Number(v));

export const AdminProductRow = ({ product, onSaved, threshold }) => {
  const [p, setP] = useState(product);
  const [open, setOpen] = useState(false);
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const dirty = EDITABLE.some((k) => !same(p[k], product[k]));

  const save = async () => {
    try {
      const body = Object.fromEntries(EDITABLE.map((k) => [k, p[k] ?? (["verdict", "description", "materials", "size", "care", "dog_size", "gtin", "mpn"].includes(k) ? "" : k === "gallery" ? [] : p[k])]));
      const { data } = await api.put(`/admin/products/${p.slug}`, { ...body, price: Number(p.price), cost: num(p.cost), ship_cost: num(p.ship_cost) });
      onSaved(data);
      toast.success(`${data.name} saved`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <li data-testid={ADMIN.productRow(p.slug)} className={`py-4 grid grid-cols-[56px_1fr] md:grid-cols-[56px_1.4fr_100px_2fr_auto] gap-3 items-center ${p.active ? "" : "opacity-60"}`}>
      <PhotoPicker value={p.image} onChange={(url) => set("image", url)} testId={ADMIN.rowPhoto(p.slug)} label={`Change photo for ${p.name}`} />
      <div className="min-w-0">
        <input aria-label="Name" value={p.name} onChange={(e) => set("name", e.target.value)} className={input} />
        <div className="mt-1 flex flex-wrap gap-2 text-[11px]">
          {isAiPhoto(p.image) && <span data-testid={ADMIN.aiPhotoBadge(p.slug)} className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Needs a real photo</span>}
          {p.cost == null && <span className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">No cost yet</span>}
          {lowMargin(p, threshold) && <span data-testid={ADMIN.lowMarginBadge(p.slug)} className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Low margin · {marginOf(p).toFixed(0)}%</span>}
          <button type="button" data-testid={ADMIN.detailsToggle(p.slug)} onClick={() => setOpen((o) => !o)} className="underline text-[#E06D44]">{open ? "Hide details" : "Specs, cost & photos"}</button>
        </div>
      </div>
      <label className="flex items-center gap-1 text-sm col-start-2 md:col-start-auto">$
        <input data-testid={ADMIN.priceInput(p.slug)} aria-label="Price" type="number" step="0.01" min="0.01" value={p.price} onChange={(e) => set("price", e.target.value)} className={input} />
      </label>
      <div className="relative col-start-2 md:col-start-auto">
        <input data-testid={ADMIN.verdictInput(p.slug)} aria-label="Kiddee verdict" value={p.verdict || ""} onChange={(e) => set("verdict", e.target.value)} placeholder="Kiddee's verdict" className={`${input} pr-10`} />
        <AiVerdictButton p={p} set={set} />
      </div>
      <div className="flex items-center gap-3 col-start-2 md:col-start-auto">
        <label className="flex items-center gap-1.5 text-sm"><input data-testid={ADMIN.activeToggle(p.slug)} type="checkbox" checked={p.active} onChange={(e) => set("active", e.target.checked)} /> Live</label>
        <label className="flex items-center gap-1.5 text-sm whitespace-nowrap"><input data-testid={ADMIN.favToggle(p.slug)} type="checkbox" checked={!!p.kiddee_fav} onChange={(e) => set("kiddee_fav", e.target.checked)} /> Kiddee approved</label>
        <button data-testid={ADMIN.saveButton(p.slug)} disabled={!dirty} onClick={save} className="px-4 py-2 rounded-full bg-[#1C1917] text-white text-sm disabled:opacity-30 hover:bg-[#E06D44] transition-colors">Save</button>
      </div>
      {open && <AdminProductDetails p={p} set={set} />}
    </li>
  );
};

const CATEGORIES = ["toys", "treats", "accessories", "collars", "harnesses", "leashes"];

export const AdminNewProduct = ({ onCreated }) => {
  const blank = { name: "", price: "", image: "", category: "toys", description: "", verdict: "", kiddee_fav: false };
  const [f, setF] = useState(blank);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.image) return toast.error("Please add a photo first");
    try {
      const { data } = await api.post("/admin/products", { ...f, price: Number(f.price), active: true });
      onCreated(data);
      setF(blank);
      toast.success(`${data.name} is live in the shop`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#F0EBE1] rounded-[1.5rem] p-6 grid sm:grid-cols-2 gap-3 max-w-3xl">
      <input data-testid={ADMIN.newName} required value={f.name} onChange={set("name")} placeholder="Product name" className={input} />
      <input data-testid={ADMIN.newPrice} required type="number" step="0.01" min="0.01" value={f.price} onChange={set("price")} placeholder="Price, e.g. 14.99" className={input} />
      <div className="sm:col-span-2 flex items-center gap-4">
        <PhotoPicker value={f.image} onChange={(url) => setF((s) => ({ ...s, image: url }))} testId={ADMIN.newPhoto} size="w-24 h-24" label="Upload product photo" />
        <div className="flex-1 space-y-2">
          <p className="text-sm text-stone-600">Tap the square to take or choose a photo, or paste a link:</p>
          <input data-testid={ADMIN.newImage} type="url" value={f.image.startsWith("/api/") ? "" : f.image} onChange={set("image")} placeholder="Photo link (https://…)" className={input} />
        </div>
      </div>
      <select data-testid={ADMIN.newCategory} value={f.category} onChange={set("category")} className={input}>
        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <input data-testid={ADMIN.newVerdict} value={f.verdict} onChange={set("verdict")} placeholder="Kiddee's verdict (optional)" className={input} />
      <label className="flex items-center gap-2 text-sm sm:col-span-2"><input data-testid={ADMIN.newFav} type="checkbox" checked={f.kiddee_fav} onChange={(e) => setF((s) => ({ ...s, kiddee_fav: e.target.checked }))} /> Kiddee approved (shows her seal in the shop)</label>
      <textarea data-testid={ADMIN.newDescription} value={f.description} onChange={set("description")} placeholder="Description" rows={3} className={`${input} sm:col-span-2`} />
      <button data-testid={ADMIN.newSubmit} className="sm:col-span-2 py-3 rounded-full bg-[#E06D44] text-white font-medium hover:bg-[#C95B33] transition-colors">Add product</button>
    </form>
  );
};
